import { ACTIVITY_EVENTS } from "components/jiffy/design-lab/shared/constants";
import { onMounted, onUnmounted, ref, watch, type Ref } from "vue";

type InactivityDetectorOptions = {
  interval: number;
  maxCallCount?: number;
  onInactive: () => void;
  onActive?: () => void;
  delayDetection?: number;
};

// Vue composable for detecting user inactivity
export function useUserInactivityDetector({
  interval,
  maxCallCount = Infinity,
  onInactive,
  onActive,
  delayDetection = 0
}: InactivityDetectorOptions) {
  const remainingCallsRef: Ref<number> = ref(maxCallCount);
  const timerRef: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
  const delayTimerRef: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
  const isDetectionStartedRef: Ref<boolean> = ref(false);
  const onInactiveRef: Ref<() => void> = ref(onInactive);
  const onActiveRef: Ref<(() => void) | undefined> = ref(onActive);
  const startMonitoring: Ref<boolean> = ref(false);

  const clearTimer = () => {
    if (timerRef.value) {
      clearTimeout(timerRef.value);
      timerRef.value = null;
    }
  };

  watch(
    () => onInactive,
    newCallback => {
      onInactiveRef.value = newCallback;
    }
  );

  watch(
    () => onActive,
    newCallback => {
      onActiveRef.value = newCallback;
    }
  );

  const schedule = () => {
    if (!isDetectionStartedRef.value) return;
    clearTimer();
    if (remainingCallsRef.value <= 0) return;
    timerRef.value = setTimeout(() => {
      onInactiveRef.value?.();
      remainingCallsRef.value -= 1;
      if (remainingCallsRef.value > 0) {
        schedule();
      }
    }, interval);
  };

  const handleActivity = (event: Event) => {
    // Only reset on human-initiated interactions
    const isTrusted = (event as any)?.isTrusted ?? true;
    if (!isTrusted) return;

    if (onActiveRef.value) onActiveRef.value();
    schedule();
  };

  const startDetection = () => {
    if (isDetectionStartedRef.value) return; // Already started

    isDetectionStartedRef.value = true;
    schedule();

    ACTIVITY_EVENTS.forEach(name => {
      document.addEventListener(name, handleActivity, { passive: true });
    });
  };

  onMounted(() => {
    if (startMonitoring.value) {
      if (delayDetection > 0) {
        delayTimerRef.value = setTimeout(startDetection, delayDetection);
      } else {
        startDetection();
      }
    }
  });

  onUnmounted(() => {
    clearTimer();
    if (delayTimerRef.value) {
      clearTimeout(delayTimerRef.value);
      delayTimerRef.value = null;
    }
    isDetectionStartedRef.value = false;
    ACTIVITY_EVENTS.forEach(name => {
      document.removeEventListener(name, handleActivity);
    });
  });

  watch(
    () => maxCallCount,
    newMaxCallCount => {
      remainingCallsRef.value = newMaxCallCount;
      clearTimer();
      if (isDetectionStartedRef.value) {
        schedule();
      }
    }
  );

  watch(
    () => interval,
    () => {
      if (isDetectionStartedRef.value) {
        clearTimer();
        schedule();
      }
    }
  );

  watch(
    () => startMonitoring.value,
    shouldMonitor => {
      // Only start when transitioning from false to true and not already started
      if (shouldMonitor && !isDetectionStartedRef.value) {
        if (delayDetection > 0) {
          delayTimerRef.value = setTimeout(startDetection, delayDetection);
        } else {
          startDetection();
        }
      }
      // Note: We don't stop monitoring if shouldMonitor becomes false
      // Once started, it continues until component unmounts
    },
    { immediate: false }
  );

  const stopMonitoring = () => {
    clearTimer();
    if (delayTimerRef.value) {
      clearTimeout(delayTimerRef.value);
      delayTimerRef.value = null;
    }
    isDetectionStartedRef.value = false;
    startMonitoring.value = false;
    remainingCallsRef.value = maxCallCount;
    ACTIVITY_EVENTS.forEach(name => {
      document.removeEventListener(name, handleActivity);
    });
  };

  const resetTimer = () => {
    if (isDetectionStartedRef.value) {
      clearTimer();
      remainingCallsRef.value = maxCallCount;
      schedule();
    }
  };

  return {
    triggerUserInactivityMonitoring: () => {
      startMonitoring.value = true;
    },
    stopMonitoring,
    resetTimer
  };
}
